/*    
<summary>
  This functional component "Device Status Filter" renders the filter form.
  Developer:Aashish Singh, Created Date: 04-July-2025
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import { Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import {
  DATE_FORMAT,
  deviceStatusSearchByOptions,
  fotaStatusOptions,
} from "../../../constants/common-constants";
import { IFotaDeviceStatusFilterDetails } from "../../../models/response/IFotaNewResponse";
import LoaderButton from "../../../shared-components/Button/LoaderButton";
import FormikDateFieldMUI from "../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import { fotaDeviceStatusFilterValidationSchema } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import Label from "../../../shared-components/Label/Label";
import { formatMessage } from "../../../translations/format-message";

interface IProps {
  initialValues: any;
  submitHandler: (values: IFotaDeviceStatusFilterDetails) => void;
  isFilterActive: boolean;
  modalClosed: () => void;
}

const FotaDeviceStatusFilterForm = (props: IProps) => {
  return (
    <Modal
      className="fancy-modal"
      show={true}
      centered={false}
      size="lg"
      onHide={() => {}}
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="filter" />
        </Modal.Title>
        <button
          onClick={props.modalClosed}
          data-testid="Close"
          className="Crossicons"
          title={formatMessage("close")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            className="bi bi-x"
            viewBox="0 0 16 16"
          >
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>
      </Modal.Header>
      <div className="p-0">
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validateOnBlur={false}
          validationSchema={fotaDeviceStatusFilterValidationSchema}
          onSubmit={async (values: any) => {
            let startDate =
              new Date(
                values.LastSegmentRequestTimestampStartDate?.$d
              ).toString() !== "Invalid Date"
                ? moment(values.LastSegmentRequestTimestampStartDate.$d).format(
                    DATE_FORMAT
                  )
                : values.LastSegmentRequestTimestampStartDate;
            let endDate =
              new Date(
                values.LastSegmentRequestTimestampEndDate?.$d
              ).toString() !== "Invalid Date"
                ? moment(values.LastSegmentRequestTimestampEndDate.$d).format(
                    DATE_FORMAT
                  )
                : values.LastSegmentRequestTimestampEndDate;
            let tempSearchBy = typeof values.SearchBy
              ? parseInt(values.SearchBy)
              : values.SearchBy;
            let tempValues: IFotaDeviceStatusFilterDetails = {
              SearchBy: tempSearchBy,
              ICCID: tempSearchBy == 1 ? values.ICCID : "",
              LastSegmentRequestTimestampStartDate:
                tempSearchBy == 2 ? startDate : "",
              LastSegmentRequestTimestampEndDate:
                tempSearchBy == 2 ? endDate : "",
              Status:
                tempSearchBy == 3
                  ? typeof values.Status === "string"
                    ? parseInt(values.Status)
                    : values.Status
                  : 0,
              DeviceRebootedMessage:
                tempSearchBy == 4 ? values.DeviceRebootedMessage : "",
            };
            props.submitHandler({ ...tempValues });
          }}
        >
          {({ values }) => {
            return (
              <Form className="user-form p-3">
                <div className="form-group row">
                  <Label
                    className="label-style col-sm-5 col-form-label"
                    required={true}
                    label="search_by"
                  />
                  <div className="col-sm-7">
                    <Field
                      name="SearchBy"
                      as={FormikFormSelect}
                      options={deviceStatusSearchByOptions}
                    />
                  </div>
                </div>

                <div className="">
                  {values.SearchBy == 1 && (
                    <div className="form-group row">
                      <Label
                        className="col-sm-5 label-style"
                        required={true}
                        label="iccid"
                      />
                      <div className="col-sm-7">
                        <Field
                          data-testid="ICCID"
                          className="form-control"
                          name="ICCID"
                          placeholder="iccid"
                          type="number"
                          as={FormikFormInput}
                        />
                      </div>
                    </div>
                  )}
                  {values.SearchBy == 2 && (
                    <>
                      <div className="form-group row">
                        <Label
                          className="col-sm-5 label-style"
                          required={true}
                          label="start_date"
                        />
                        <div className="col-sm-7">
                          <Field
                            name="LastSegmentRequestTimestampStartDate"
                            as={FormikDateFieldMUI}
                            disableFuture={false}
                            // min={moment()
                            //   .subtract(3, "months")
                            //   .format(DATE_FORMAT)}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <Label
                          className="col-sm-5 label-style"
                          required={true}
                          label="end_date"
                        />
                        <div className="col-sm-7">
                          <Field
                            name="LastSegmentRequestTimestampEndDate"
                            as={FormikDateFieldMUI}
                            disableFuture={false}
                            // min={moment()
                            //   .subtract(3, "months")
                            //   .format(DATE_FORMAT)}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {values.SearchBy == 3 && (
                    <div className="form-group row">
                      <Label
                        className="col-sm-5"
                        required={true}
                        label="status"
                      />
                      <div className="col-sm-7">
                        <Field
                          data-testid="Status"
                          id="custom"
                          className="form-control"
                          name="Status"
                          as={FormikFormSelect}
                          options={fotaStatusOptions}
                        />
                      </div>
                    </div>
                  )}
                  {values.SearchBy == 4 && (
                    <div className="form-group row">
                      <Label
                        className="col-sm-5 label-style"
                        required={true}
                        label="device_rebooted_message"
                      />
                      <div className="col-sm-7">
                        <Field
                          data-testid="DeviceRebootedMessage"
                          className="form-control"
                          name="DeviceRebootedMessage"
                          placeholder="device_rebooted_message"
                          type="text"
                          as={FormikFormInput}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-end pb-2">
                  <button
                    data-testid="apply"
                    className="btn btn-secondary"
                    type="button"
                    onClick={props.modalClosed}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <FormattedMessage id="button_cancel" />
                  </button>

                  <LoaderButton type="submit" className="ms-2" text="apply" />
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};

export default observer(FotaDeviceStatusFilterForm);
